import { StepType, TourProvider } from "@reactour/tour";
import { CompanyParams } from "const/routes";
import { TourStepContent } from "pages/Companies/CompanyId/Dashboard/Overview/components/TourStepContent";
import { useEmployeesTourSteps } from "pages/Companies/CompanyId/Employees/hooks/use-tour-steps.hooks";
import type { FC, PropsWithChildren } from "react";
import { useEffect, useState } from "react";

import { useParams } from "react-router-dom";
import { TourType } from "types";

export const EmployeesTourProvider: FC<PropsWithChildren> = ({ children }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [tourReady, setTourReady] = useState(false);

  const { companyId } = useParams<CompanyParams>() as CompanyParams;

  const { steps: employeesTourSteps } = useEmployeesTourSteps(companyId);

  useEffect(() => {
    const checkElementsAvailable = async () => {
      if (employeesTourSteps.length === 0) {
        return false;
      } else if (tourReady) {
        return true;
      }
      const selectors = employeesTourSteps.map((step) => step.selector);

      const allElementsExist = selectors.every((selector) => {
        return document.querySelector(selector) !== null;
      });

      setTourReady(allElementsExist);
      return allElementsExist;
    };

    void checkElementsAvailable();

    const interval = setInterval(async () => {
      const allElementsExist = await checkElementsAvailable();
      if (allElementsExist) {
        clearInterval(interval);
      }
    }, 1000);

    const observer = new MutationObserver(checkElementsAvailable);
    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });

    return () => {
      clearInterval(interval);
      observer.disconnect();
    };
  }, [employeesTourSteps]);

  if (!tourReady) {
    return <>{children}</>;
  }

  const steps: StepType[] = employeesTourSteps.map((step, index) => ({
    selector: step.selector,
    content: (
      <TourStepContent
        currentStep={index}
        heading={step.title}
        description={step.description}
        steps={employeesTourSteps.length}
        setCurrentStep={setCurrentStep}
        tourType={TourType.ManageEmployees}
      />
    ),
  }));

  return (
    <TourProvider
      steps={steps}
      showBadge={false}
      showCloseButton={false}
      showDots={false}
      showNavigation={false}
      padding={{
        mask: 0,
      }}
      currentStep={currentStep}
      styles={{
        popover: (base) => ({
          ...base,
          padding: 0,
          marginLeft: 10,
          borderRadius: 13,
          color: "black",
        }),
        maskArea: (base) => ({ ...base, rx: 13 }),
      }}
    >
      {children}
    </TourProvider>
  );
};
